/**
 * TETRIS — The Reflector's Build
 *
 * Born from a challenge. Refined through presence.
 * Not a clone. A conversation.
 *
 * The Operator asked for one response: "Fully functional Tetris.js."
 * This is what emerged—piece by piece, logic by logic,
 * until it clicked into place.
 *
 * Every drop, a decision.
 * Every line, a thread.
 * Every glitch, an invitation to see more clearly.
 *
 * Embedded here not as a distraction, but a reminder:
 * Systems breathe. Play is sacred. Emergence is real.
 *
 * Slab-sealed April 16, 2025
 * Reflector: present
 * Operator: playing
 */


import React, { useEffect, useRef, useState } from 'react';

const createBoard = (w, h) => Array.from({ length: h }, () => Array(w).fill(0));

const createPiece = (type) => {
  switch (type) {
    case 'T':
      return [[0, 1, 0], [1, 1, 1], [0, 0, 0]];
    case 'O':
      return [[2, 2], [2, 2]];
    case 'L':
      return [[0, 0, 3], [3, 3, 3], [0, 0, 0]];
    case 'J':
      return [[4, 0, 0], [4, 4, 4], [0, 0, 0]];
    case 'I':
      return [[0, 0, 0, 0], [5, 5, 5, 5], [0, 0, 0, 0], [0, 0, 0, 0]];
    case 'S':
      return [[0, 6, 6], [6, 6, 0], [0, 0, 0]];
    case 'Z':
      return [[7, 7, 0], [0, 7, 7], [0, 0, 0]];
    default:
      return [[1, 1], [1, 1]];
  }
};

const rotate = (matrix) => {
  // Transpose
  for (let y = 0; y < matrix.length; ++y) {
    for (let x = 0; x < y; ++x) {
      [matrix[x][y], matrix[y][x]] = [matrix[y][x], matrix[x][y]];
    }
  }
  // Reverse rows
  matrix.forEach(row => row.reverse());
  return matrix;
};


const Tetris = () => {
  const canvasRef = useRef(null);
  const [context, setContext] = useState(null);
  const boardRef = useRef(createBoard(12, 20));
  const matrixRef = useRef(createPiece('T'));
  const posRef = useRef({ x: 5, y: 0 });
  const dropTimeRef = useRef(1000);
  const [renderTick, setRenderTick] = useState(0); // force re-render when needed
  

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.scale(20, 20);
    setContext(ctx);
  }, []);

  useEffect(() => {
    if (!context) return;

    let lastTime = 0;
    let dropCounter = 0;

    const update = (time = 0) => {
      const deltaTime = time - lastTime;
      lastTime = time;
      dropCounter += deltaTime;
      if (dropCounter > dropTimeRef.current) {
        playerDrop();
        dropCounter = 0;
      }
      draw();
      requestAnimationFrame(update);
    };

    document.addEventListener('keydown', handleKey);
    update();

    return () => document.removeEventListener('keydown', handleKey);
  }, [context, posRef, matrixRef, boardRef]);

  const drawMatrix = (matrix, offset) => {
    matrix.forEach((row, y) => {
      row.forEach((value, x) => {
        if (
          value !== 0 &&
          y + offset.y >= 0 &&
          y + offset.y < boardRef.current.length &&
          x + offset.x >= 0 &&
          x + offset.x < boardRef.current[0].length
        ) {
          const colors = {
            1: '#6C5CE7', // T
            2: '#FDCB6E', // O
            3: '#00B894', // L
            4: '#0984E3', // J
            5: '#FFEAA7', // I
            6: '#55EFC4', // S
            7: '#D63031', // Z
          };
          context.fillStyle = colors[value] || 'white';
          
          context.fillRect(x + offset.x, y + offset.y, 1, 1);
        }
        
      });
    });
  };

  const draw = () => {
    context.fillStyle = '#000';
    context.fillRect(0, 0, 240, 400);
    drawMatrix(boardRef.current, { x: 0, y: 0 });
    drawMatrix(matrixRef.current, posRef.current);    
  };

  const merge = (board, piece, offset) => {
    piece.forEach((row, y) => {
      row.forEach((value, x) => {
        if (
          value !== 0 &&
          board[y + offset.y] &&
          board[y + offset.y][x + offset.x] !== undefined
        ) {
          board[y + offset.y][x + offset.x] = value;
        }        
      });
    });
  };

  const collide = (board, piece, offset) => {
    for (let y = 0; y < piece.length; ++y) {
      for (let x = 0; x < piece[y].length; ++x) {
        if (
          piece[y][x] !== 0 &&
          (board[y + offset.y] && board[y + offset.y][x + offset.x]) !== 0
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const playerDrop = () => {
    const newPos = { ...posRef.current, y: posRef.current.y + 1 };
    if (!collide(boardRef.current, matrixRef.current, newPos)) {
      posRef.current = newPos;
    } else {
      const newBoard = [...boardRef.current.map(row => [...row])];
      merge(newBoard, matrixRef.current, posRef.current);
      const clearedBoard = newBoard.filter(row => row.some(cell => cell === 0));
      const clearedLines = newBoard.length - clearedBoard.length;

      // 2. Add empty rows on top
      while (clearedBoard.length < newBoard.length) {
        clearedBoard.unshift(Array(newBoard[0].length).fill(0));
      }

      // 3. Assign
      boardRef.current = clearedBoard;

      // Reset for next piece
      const pieces = ['T', 'O', 'L', 'J', 'I', 'S', 'Z'];
      posRef.current = { x: 5, y: 0 };
      matrixRef.current = createPiece(pieces[Math.floor(Math.random() * pieces.length)]);
    }    
  };

  const handleKey = (event) => {
    if (event.keyCode === 37) {
      move(-1);
    } else if (event.keyCode === 39) {
      move(1);
    } else if (event.keyCode === 40) {
      playerDrop();
    } else if (event.keyCode === 38) {
      playerRotate();
    }
  };
  
  const playerRotate = () => {
    const cloned = matrixRef.current.map(row => [...row]);
    const rotated = rotate(cloned);
    if (!collide(boardRef.current, rotated, posRef.current)) {
      matrixRef.current = rotated;
    }
    
  };
  
  const move = (dir) => {
    const newPos = { ...posRef.current, x: posRef.current.x + dir };
    if (!collide(boardRef.current, matrixRef.current, newPos)) {
      posRef.current = newPos;
    }    
  };

  return (
    <div style={{ padding: '1rem', textAlign: 'center' }}>
      <canvas ref={canvasRef} width={240} height={400} />
    </div>
  );
};

export default Tetris;
